import type {
  AdminStandard,
  AdminStandardsResponse,
  CreateAdminStandardBody,
  UpdateAdminStandardBody,
} from '@/api/(admin)/standards/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Standards, all on `API_ROOT_URL`.
 *
 * The list and the writes sit on different paths and that is not a mistake:
 * `GET /api/standards` is the customer-facing list (the certificate pickers
 * and the certified-materials order lines read it too), while the writes are
 * admin-only under `/api/admin/standards`. Because the list is shared, this
 * module does not reshape its query — it forwards a pre-built string and
 * leaves the parameters to the hook.
 */

/**
 * The list. Takes a pre-built query string; paging, sort, search and filter
 * live in the hook. Each row is the full standard, so it can seed the edit
 * sheet.
 */
export async function fetchAdminStandards(
  qs: string,
): Promise<AdminStandardsResponse> {
  return apiFetch<AdminStandardsResponse>(
    `/api/standards?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * Creates one. `type` is accepted here and nowhere else. Answers 201 with the
 * `{ data, metadata }` envelope.
 */
export async function createAdminStandard(
  body: CreateAdminStandardBody,
): Promise<{ data: AdminStandard }> {
  return apiFetch<{ data: AdminStandard }>(
    '/api/admin/standards',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Updates one. A PUT, and it will not change the standard's `type`. Answers
 * the `{ data, metadata }` envelope.
 */
export async function updateAdminStandard(
  id: string,
  body: UpdateAdminStandardBody,
): Promise<{ data: AdminStandard }> {
  return apiFetch<{ data: AdminStandard }>(
    `/api/admin/standards/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
